<template>
  <div class="domain-edit-modal">
    <h4 class="modal-heading">{{ $t("domainEditModal.changeDomain") }}</h4>
    <div class="options-container">
      <div
        class="option-section"
        :class="{
          active: domainChangeMode === DOMAIN_ACTIONS.CHANGE_EXISTING_DOMAIN,
        }"
        @click="domainChangeMode = DOMAIN_ACTIONS.CHANGE_EXISTING_DOMAIN"
      >
        <div class="option-header">
          <input
            type="radio"
            id="fix-domain"
            name="domain-mode"
            value="fix"
            v-model="domainChangeMode"
          />
          <div class="option-container">
            <div class="option-label">
              {{ $t("domainEditModal.changeDomainNameLabel") }}
            </div>

            <div
              v-if="domainChangeMode === DOMAIN_ACTIONS.CHANGE_EXISTING_DOMAIN"
              class="option-details"
            >
              <div class="input-wrapper">
                <input
                  type="text"
                  class="input"
                  :class="{ 'b-red': hasError }"
                  v-model="newDomainName"
                  v-bind:placeholder="$t('domainEditModal.inputPlaceholder')"
                  :disabled="processing"
                  ref="domainInput"
                />
                <div class="error" :class="{ hidden: !hasError }" v-html="errorMessage" />
                <div class="checkbox-container">
                  <input
                    type="checkbox"
                    id="ownership-verification"
                    v-model="domainOwnershipVerified"
                  />
                  <label
                    for="ownership-verification"
                    v-html="
                      $tc('domainEditModal.checkboxLabel', 1, {
                        0: `<span class='domain-name'>${
                          newDomainName || newDomainNameFallback
                        }</span>`,
                      })
                    "
                  ></label>
                </div>
              </div>
              <div class="button-wrapper">
                <button
                  :disabled="!domainOwnershipVerified || newDomainName === '' || processing"
                  class="btn btn-primary"
                  :class="{ processing: processing }"
                  @click="handleUpdateDomainBtnClick"
                >
                  <span v-if="newDomainName !== ''">
                    {{ $t("domainEditModal.btnText", [newDomainName]) }}
                  </span>
                  <span v-else>{{ $t("genericText.update") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            domainChangeMode === DOMAIN_ACTIONS.CHANGE_EXISTING_DOMAIN &&
            (hasMailAndSite || hasOnlySite)
          "
          class="info-text"
        >
          <div class="info-icon"></div>
          <div v-if="hasMailAndSite">
            {{ $t("domainEditModal.dataEraseInfo") }}
          </div>
          <div v-else-if="hasOnlySite" v-html="$t('domainEditModal.siteDataEraseWarning')"></div>
        </div>
      </div>

      <div
        class="option-section"
        :class="{
          active: domainChangeMode === DOMAIN_ACTIONS.PURCHASE_NEW_DOMAIN,
        }"
        @click="domainChangeMode = DOMAIN_ACTIONS.PURCHASE_NEW_DOMAIN"
      >
        <div class="option-header">
          <input
            type="radio"
            id="new-domain"
            name="domain-mode"
            value="new"
            v-model="domainChangeMode"
          />
          <div class="option-container">
            <div class="option-label">
              {{ $t("domainEditModal.purchaseNewDomainHeading") }}
            </div>

            <div
              v-if="domainChangeMode === DOMAIN_ACTIONS.PURCHASE_NEW_DOMAIN"
              class="option-details"
            >
              <div class="input-wrapper">
                <input
                  type="text"
                  class="input"
                  v-model="newCoSiteDomain"
                  v-bind:placeholder="$t('domainEditModal.newDomainPlaceholder')"
                  ref="newDomainInput"
                  @keyup.enter="newCoSiteDomain?.trim() && handleGetNewDomainClick()"
                />
              </div>

              <div class="button-wrapper">
                <button
                  :disabled="!newCoSiteDomain?.trim()"
                  class="btn btn-primary"
                  @click="handleGetNewDomainClick"
                >
                  {{ $t("domainEditModal.getNewDomainBtn") }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="info-text" v-if="domainChangeMode === DOMAIN_ACTIONS.PURCHASE_NEW_DOMAIN">
          <div class="info-icon"></div>
          {{ $t("domainEditModal.existingDomainCancellationInfo", [currentDomain]) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DomainEditSuccessModal from "@/components/DomainActivation/DomainEditModal/DomainEditSuccessModal.vue";
import {
  DOMAIN_UNAVAILABILITY_ERROR_MAP,
  domainUnavailableReasons,
  ERROR_KEYS,
} from "@/components/DomainActivation/DomainEditModal/DomainEditHelpers";

import {
  calculateDateDifference,
  getCurrentUserEmail,
  hasMailSuiteProduct,
  hasProtocolOrWwwSubdomain,
  hasSiteProduct,
  isValidDomain,
  PRODUCTS,
} from "@/helpers";
import { openNeoSignupApp, openNeoSignupAppSitePurchaseFlow } from "@/helpers/neoWebsiteHelpers";
import { COMMON_MODAL_EMITTERS } from "@/helpers/const";
import eventBus from "@/helpers/event-bus";
import bundleEvents from "@/telemetry/bundleEvents";
import { MEDUSA_EVENTS_MAP } from "@/telemetry/events";
import { getLambdaUrl, LAMBDA_ENTITIES, LAMBDA_FUNCTION_NAMES } from "@/helpers/lambdaHelpers";
import { getCommonMedusaParams, sendOrderAndCustomerEvent } from "@/telemetry/medusaUtils";
import { postDomainChangeRequestToFlockChannel } from "@/helpers/webhookHelpers";
import { mapGetters } from "vuex";
import { removeSpace } from "@/helpers/stringHelpers";

const getErrorMessages = vm => {
  return {
    EMPTY_DOMAIN: vm.$t("domainEditModal.errorMessages.emptyDomain"),
    NO_CHANGE: vm.$t("domainEditModal.errorMessages.noChange"),
    DOMAIN_NOT_AVAILABLE: vm.$t("domainEditModal.errorMessages.domainNotAvailable"),
    DOMAIN_IN_USE: vm.$t("domainEditModal.errorMessages.domainInUse"),
    DOMAIN_NOT_REGISTERED: vm.$t("domainEditModal.errorMessages.domainNotRegistered"),
    UNVERIFIED_DOMAIN: vm.$t("domainEditModal.errorMessages.unverifiedDomain"),
    REQUEST_UNSUCCESSFUL: vm.$t("domainEditModal.errorMessages.requestUnsuccessful"),
    INVALID_DOMAIN: vm.$t("domainEditModal.errorMessages.invalidDomain"),
    HAS_PROTOCOL: vm.$t("domainEditModal.errorMessages.hasProtocol"),
    SHARED_SUBDOMAIN: vm.$t("domainEditModal.errorMessages.sharedSubdomain"),
  };
};
const { CHECK_MAIL_ORDER_AVAILABILITY, CHECK_DOMAIN_REGISTRATION } = LAMBDA_FUNCTION_NAMES;
const { NEO } = LAMBDA_ENTITIES;

const { CHANGE_DOMAIN_AVAILABILITY_CHECKED, CHANGE_DOMAIN_REQUESTED, DOMAIN_AVAILABILITY_CHECKED } =
  MEDUSA_EVENTS_MAP;

const DOMAIN_NAME_FALLBACK = "example.com";

export default {
  name: "DomainEditModal",
  data() {
    return {
      currentDomain: this.domainName,
      domainOwnershipVerified: false,
      errorMessage: "",
      hasError: false,
      newDomainName: this.currentDomain,
      newDomainNameFallback: DOMAIN_NAME_FALLBACK,
      newDomainNameservers: null,
      processing: false,
      userEmail: getCurrentUserEmail(),
      domainChangeMode: "fix", // Default mode: 'fix'
      newCoSiteDomain: "",
      DOMAIN_ACTIONS: {
        CHANGE_EXISTING_DOMAIN: "fix",
        PURCHASE_NEW_DOMAIN: "new",
      },
    };
  },
  props: ["sourceHook"],
  methods: {
    resetError() {
      this.hasError = false;
      this.errorMessage = "";
    },
    showErrorMessage(messageKey) {
      this.hasError = true;
      this.errorMessage = getErrorMessages(this)[messageKey];
    },
    setProcessing(isProcessing) {
      this.processing = isProcessing;
    },
    isDomainValid() {
      const newDomainTrimmed = this.newDomainName.trim();
      if (!newDomainTrimmed) {
        // show empty error
        this.showErrorMessage(ERROR_KEYS.EMPTY_DOMAIN);
        return false;
      }

      if (hasProtocolOrWwwSubdomain(newDomainTrimmed)) {
        // show protocol error
        this.showErrorMessage(ERROR_KEYS.HAS_PROTOCOL);
        return false;
      }

      if (!isValidDomain(newDomainTrimmed)) {
        this.showErrorMessage(ERROR_KEYS.INVALID_DOMAIN);
        return false;
      }

      if (newDomainTrimmed === this.currentDomain) {
        this.showErrorMessage(ERROR_KEYS.NO_CHANGE);
        this.logChangeDomainAvailabilityCheckMedusa(
          false,
          DOMAIN_UNAVAILABILITY_ERROR_MAP.NO_CHANGE
        );
        return false;
      }

      return true;
    },
    checkDomainRegistration() {
      const url = getLambdaUrl(NEO, CHECK_DOMAIN_REGISTRATION);
      return this.http
        .postData(url, {
          domainName: this.newDomainName?.trim(),
        })
        .then(res => {
          const { registered, nameServers = null } = res;
          this.newDomainNameservers = nameServers && nameServers.join(", ");
          return registered;
        })
        .catch(err => {
          console.error(err);
          // we will not block the user in case the registration check fails for whatever reason
          return true;
        });
    },
    checkDomainAvailability() {
      return new Promise((resolve, reject) => {
        const url = getLambdaUrl(NEO, CHECK_MAIL_ORDER_AVAILABILITY);

        this.http.postData(
          url,
          {
            domainName: this.newDomainName?.trim(),
          },
          {},
          res => {
            if (res.statusCode === 200) {
              const { isDomainAvailable, unAvailabilityReason = "" } = res;

              this.logChangeDomainAvailabilityCheckMedusa(isDomainAvailable, unAvailabilityReason);

              resolve(res);
            } else {
              reject(new Error("Unexpected status code: " + res.statusCode));
            }
          },
          err => {
            if (err.data?.statusCode === 400) {
              const { code } = err.data;
              if (code === DOMAIN_UNAVAILABILITY_ERROR_MAP.DISALLOWED_DOMAIN) {
                this.logChangeDomainAvailabilityCheckMedusa(false, code);
              }
              reject(err.data);
            } else {
              reject(err);
            }
          }
        );
      });
    },
    async handleUpdateDomainBtnClick() {
      this.resetError();

      if (!this.domainOwnershipVerified || !this.isDomainValid()) {
        return;
      }

      this.setProcessing(true);

      /** Check if the domain is a valid, registered domain **/
      const isDomainRegistered = await this.checkDomainRegistration();
      if (!isDomainRegistered) {
        this.showErrorMessage("DOMAIN_NOT_REGISTERED");
        this.logChangeDomainAvailabilityCheckMedusa(
          false,
          DOMAIN_UNAVAILABILITY_ERROR_MAP.UNREGISTERED_DOMAIN
        );
        this.setProcessing(false);
        return;
      }

      /** Check if the domain is available or already attached to Neo **/
      try {
        const domainAvailability = await this.checkDomainAvailability();
        const {
          isDomainAvailable = false,
          unAvailabilityReason = "",
          code = ERROR_KEYS.REQUEST_UNSUCCESSFUL,
        } = domainAvailability;

        if (isDomainAvailable) {
          const domainDetails = {
            oldDomain: this.currentDomain,
            newDomain: this.newDomainName,
            bundleId: this.bundleId,
            email: this.userEmail,
          };

          const medusaPayload = {
            source_hook: this.sourceHook,
            new_domain: this.newDomainName,
          };

          this.logMedusaEvent(CHANGE_DOMAIN_REQUESTED, medusaPayload);

          bundleEvents.logChangeDomainRequested(medusaPayload);

          postDomainChangeRequestToFlockChannel(domainDetails).then(res => {
            const { requestSuccessful } = res;
            if (requestSuccessful) {
              // close current modal and show success screen
              eventBus.$emit(COMMON_MODAL_EMITTERS.OPEN_MODAL, {
                component: DomainEditSuccessModal,
                props: {
                  newDomainName: this.newDomainName,
                },
                modalWidth: "500px",
                replaceCurrentModal: true,
              });
            } else {
              // show error message
              this.showErrorMessage(ERROR_KEYS.REQUEST_UNSUCCESSFUL);
              this.setProcessing(false);
            }
          });
        } else {
          // The domain is not available, show error based on reason or default to generic reason as fallback
          let errorMessageKey =
            DOMAIN_UNAVAILABILITY_ERROR_MAP[unAvailabilityReason] || ERROR_KEYS[code];

          this.showErrorMessage(errorMessageKey);
          this.setProcessing(false);
        }
      } catch (e) {
        this.showErrorMessage(ERROR_KEYS[e.code] || ERROR_KEYS.REQUEST_UNSUCCESSFUL);
        this.setProcessing(false);
      }
    },
    logChangeDomainAvailabilityCheckMedusa(isDomainAvailable, unAvailabilityReason) {
      const { autoRenew, expiresOn, isTrial, noOfAccounts } = this.currentPlanDetails;

      let commonPayload = {
        auto_renew_enabled: autoRenew,
        domain_name_length: this.newDomainName.split(".")[0].length,
        domain_nameserver: this.newDomainNameservers,
        mailbox_count: noOfAccounts?.active,
        new_domain: this.newDomainName,
        source_hook: this.sourceHook,
      };

      if (isTrial) {
        const freeTrialDaysRemaining = calculateDateDifference(expiresOn);
        commonPayload = {
          ...commonPayload,
          free_trial_days_left: freeTrialDaysRemaining,
        };
      }

      let finalPayload;
      if (isDomainAvailable) {
        finalPayload = {
          ...commonPayload,
          domain_availability: "Available",
        };
        this.logMedusaEvent(CHANGE_DOMAIN_AVAILABILITY_CHECKED, finalPayload, false);
        this.logMedusaEvent(
          DOMAIN_AVAILABILITY_CHECKED,
          {
            ...finalPayload,
            user_type: "customer",
          },
          false
        );
        this.logBundleMedusaEvent(finalPayload);
      } else {
        finalPayload = {
          ...commonPayload,
          domain_availability:
            domainUnavailableReasons[unAvailabilityReason].event_availability_message,
          error: domainUnavailableReasons[unAvailabilityReason].event_error_message,
        };
        this.logMedusaEvent(CHANGE_DOMAIN_AVAILABILITY_CHECKED, finalPayload, false);
        this.logMedusaEvent(
          DOMAIN_AVAILABILITY_CHECKED,
          {
            ...finalPayload,
            user_type: "customer",
          },
          false
        );
        this.logBundleMedusaEvent(finalPayload);
      }
    },
    logBundleMedusaEvent(payload) {
      bundleEvents.logDomainAvailabilityChecked({
        ...payload,
        action: "Change domain",
      });
    },
    logMedusaEvent(eventName, params, sendBothOrderAndCustomer = true) {
      if (sendBothOrderAndCustomer) {
        sendOrderAndCustomerEvent.call(this, {
          eventName,
          ...params,
        });
      } else {
        this.medusaEvents.trackEvent({
          ...getCommonMedusaParams(),
          eventName,
          ...params,
        });
      }
    },
    handleGetNewDomainClick() {
      bundleEvents.logChangeDomainCoSite({
        source_hook: this.sourceHook,
        action: "Get this domain",
      });
      const params = {
        source: "change-cd",
        utm_source: "neo-admin",
        domain: removeSpace(this.newCoSiteDomain),
      };

      if (hasMailSuiteProduct(this.products)) {
        // Redirect to Mail flow
        openNeoSignupApp(params, "domain-selection");
        return;
      }
      // Redirect to Site flow
      openNeoSignupAppSitePurchaseFlow(params);
    },
  },
  created() {
    this.currentDomain = this.domainName;
    this.newDomainName = this.domainName;
  },
  mounted() {
    if (this.domainChangeMode === this.DOMAIN_ACTIONS.CHANGE_EXISTING_DOMAIN) {
      this.$nextTick(() => {
        if (this.$refs.domainInput) {
          this.$refs.domainInput.focus();
        }
      });
    }
  },
  watch: {
    domainChangeMode(newVal) {
      this.$nextTick(() => {
        if (newVal === this.DOMAIN_ACTIONS.CHANGE_EXISTING_DOMAIN && this.$refs.domainInput) {
          this.$refs.domainInput.focus();
        } else if (
          newVal === this.DOMAIN_ACTIONS.PURCHASE_NEW_DOMAIN &&
          this.$refs.newDomainInput
        ) {
          this.$refs.newDomainInput.focus();
        }
      });
    },
  },
  computed: {
    ...mapGetters(["currentPlanDetails", "products", "isAllProductsActive", "activeProducts"]),
    ...mapGetters({
      bundleId: "bundle/bundleId",
      domainName: "domain/domainName",
      siteProductDetails: "site/currentProductDetails",
    }),
    hasMailAndSite() {
      return (
        hasMailSuiteProduct(this.products) &&
        hasSiteProduct(this.products) &&
        this.isAllProductsActive
      );
    },
    hasOnlySite() {
      return (
        hasSiteProduct(this.products) &&
        !hasMailSuiteProduct(this.products) &&
        this.activeProducts.includes(PRODUCTS.SITE)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.domain-edit-modal {
  padding: 24px;
  font-size: 14px;
  line-height: 22px;

  .modal-heading {
    margin-bottom: 20px;
    font-size: 24px;
  }

  .options-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .option-section {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--borderLightGray);
    border-radius: 8px;
    padding: 24px 20px;
    cursor: pointer;

    &.active {
      background-color: var(--lightBlueBg3);
      border-color: var(--lightBlueBorder2);
      cursor: default;
    }

    .option-header {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      input[type="radio"] {
        margin-top: 3px;
        cursor: pointer;
        flex-shrink: 0;
      }

      .option-container {
        flex: 1;
      }
    }

    .option-label {
      font-size: 16px;
      font-weight: 600;
      line-height: 150%;
    }
  }

  .input-wrapper {
    margin: 12px 0 20px;

    .input {
      &[disabled] {
        background: var(--readOnlyTxtBgCol);
        user-select: none;
      }
    }

    .checkbox-container {
      display: flex;
      gap: 10px;
      margin-top: 10px;
      align-items: flex-start;

      input[type="checkbox"] {
        margin-top: 5px;
      }
    }

    ::v-deep .domain-name {
      font-weight: 600;
      display: inline-block;
      word-break: break-word;
    }

    .error {
      margin-top: 12px;
      font-size: 14px;

      ::v-deep a {
        font-weight: 600;
      }

      &.hidden {
        display: none;
      }
    }
  }

  .info-text {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 25px;
    line-height: 22px;
    border-top: 1px solid var(--borderGray);
    padding-top: 24px;
    font-size: 16px;
    font-weight: 500;
    line-height: 150%;

    .info-icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      margin-top: 2px;
      background: url("../../../assets/info-i.svg") no-repeat center / contain;
    }
  }

  .button-wrapper {
    .btn {
      font-size: 14px;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      word-wrap: normal;
      height: 40px;

      span {
        color: inherit;
      }

      &.processing {
        background: var(--primaryBtnCol) url("../../../assets/loading-spinner-white.svg") no-repeat
          10px center / 20px;
        padding-left: 35px;
      }

      &[disabled] {
        opacity: 0.5;
      }

      &:hover {
        color: var(--primaryBtnTextCol);
      }
    }
  }
}

@import "../../../styles/variables.scss";
@media screen and (max-width: $responsive-breakpoint) {
  .domain-edit-modal {
    padding: 0;

    .option-section {
      padding: 16px;
    }

    .info-text {
      font-size: 15px;
    }
  }
}
</style>
